"use client";

import { useState, type FormEvent } from "react";
import { useRouter } from "next/navigation";
import { signIn } from "@/lib/auth-client";
import { Alert, Knopf, Passwortfeld, Textfeld } from "@horn/ui-preline/bausteine";
import { beschreibeAnmeldefehler, MICROSOFT_FEHLSCHLAG, UNBEKANNTER_FEHLSCHLAG } from "./anmeldefehler";

/**
 * Anmeldeformular (lokal + Microsoft). Ablauf unverändert zum Bestand:
 * signIn.email → /post-login (+ refresh); Microsoft über signIn.social mit
 * callbackURL /post-login und errorCallbackURL /login. Neu ist nur die
 * Darstellung (Preline) und die Fehlerform (r105: Satz statt Rohtext/Code).
 */
export function LoginForm({ entraEnabled }: { entraEnabled: boolean }) {
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const result = await signIn.email({
        email,
        password,
      });

      if (result.error) {
        setError(beschreibeAnmeldefehler(result.error));
        setLoading(false);
        return;
      }

      // Server-side Route holt den letzten Pfad und redirectet
      router.push("/post-login");
      router.refresh();
    } catch (err) {
      console.error("[login] Anmeldung fehlgeschlagen", err);
      setError(UNBEKANNTER_FEHLSCHLAG);
      setLoading(false);
    }
  }

  async function onMicrosoftSignIn() {
    setError(null);
    setLoading(true);
    try {
      // Better-Auth liefert { data: { url?, redirect? }, error } zurueck und
      // navigiert NUR automatisch, wenn `data.redirect === true`.
      const result = await signIn.social({
        provider: "microsoft",
        callbackURL: "/post-login",
        // Fehler im Callback zurueck zu /login mit ?error=..., dort menschlich gerendert.
        errorCallbackURL: "/login",
      });

      const data = (result as { data?: { url?: string; redirect?: boolean } | null }).data;
      const fehler = (result as { error?: { message?: string } | null }).error;

      if (fehler) {
        console.error("[login] Microsoft-Anmeldung: Serverfehler", fehler);
        setError(MICROSOFT_FEHLSCHLAG);
        setLoading(false);
        return;
      }

      // Fallback: wenn der Client nicht selbst redirected hat, manuell navigieren.
      if (data?.url && typeof window !== "undefined") {
        window.location.href = data.url;
        return;
      }

      // Weder Fehler noch URL — typisch: Provider serverseitig nicht registriert
      // (OIDC-Konfiguration nach dem Start geändert, Better-Auth liest sie nur beim Boot).
      console.error("[login] Microsoft-Anmeldung lieferte keine Weiterleitung (Server-Neustart nötig?)");
      setError(MICROSOFT_FEHLSCHLAG);
      setLoading(false);
    } catch (err) {
      console.error("[login] Microsoft-Anmeldung fehlgeschlagen", err);
      setError(MICROSOFT_FEHLSCHLAG);
      setLoading(false);
    }
  }

  const absendenGesperrt = loading || !email || password.length < 12;

  return (
    <div className="space-y-6">
      {/* Preline: component/dividers/dividers-vertical-button-grpup-with-label */}
      {entraEnabled && (
        <div>
          <Knopf
            groesse="lg"
            volleBreite="immer"
            onClick={onMicrosoftSignIn}
            disabled={loading}
            symbol={
              <svg className="shrink-0 size-4" width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
              </svg>
            }
          >
            Mit Microsoft anmelden
          </Knopf>

          <div className="py-3 flex items-center text-xs text-muted-foreground-2 uppercase before:flex-1 before:border-t before:border-line-2 before:me-6 after:flex-1 after:border-t after:border-line-2 after:ms-6">
            oder lokal anmelden
          </div>
        </div>
      )}

    <form onSubmit={onSubmit} className="space-y-8">
      {/* Preline: ecommerce/storefront/login.html#log-in-details */}
      <div className="space-y-3">
        <Textfeld
          id="anmelden-email"
          label="E-Mail"
          art="email"
          required
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />

        <Passwortfeld
          id="anmelden-passwort"
          label="Passwort"
          required
          autoComplete="current-password"
          minLength={12}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />

        {error && (
          <Alert ton="fehler" id="anmelden-fehler">
            {error}
          </Alert>
        )}
      </div>

      <div className="space-y-4">
        <Knopf type="submit" variante="primaer" groesse="lg" volleBreite="immer" disabled={absendenGesperrt} laedt={loading}>
          {loading ? "Anmelden …" : "Anmelden"}
        </Knopf>
      </div>
    </form>
    </div>
  );
}
